import { create } from "zustand";
import { api } from "../lib/api";

export type IntegrationProvider = "gmail" | "calendar" | "drive";

export interface IntegrationStatus {
  provider: IntegrationProvider;
  connected: boolean;
  account_email: string | null;
  enabled: boolean;
}

interface IntegrationsState {
  integrations: IntegrationStatus[];
  loading: boolean;
  error: string | null;

  fetchIntegrations: () => Promise<void>;
  startOAuth: () => Promise<void>;
  disconnect: (provider: IntegrationProvider) => Promise<void>;
  handleOAuthCallback: (code: string, provider: IntegrationProvider) => Promise<void>;
}

const DEFAULT_INTEGRATIONS: IntegrationStatus[] = [
  { provider: "gmail", connected: false, account_email: null, enabled: false },
  { provider: "calendar", connected: false, account_email: null, enabled: false },
  { provider: "drive", connected: false, account_email: null, enabled: false },
];

export const useIntegrationsStore = create<IntegrationsState>()((set) => ({
  integrations: DEFAULT_INTEGRATIONS,
  loading: false,
  error: null,

  fetchIntegrations: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.get<{ integrations: IntegrationStatus[] }>("/api/v1/integrations");
      set({ integrations: data.integrations, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : "Failed to load integrations" });
    }
  },

  startOAuth: async () => {
    try {
      const data = await api.get<{ url: string }>("/api/v1/integrations/oauth/start");
      window.location.href = data.url;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "OAuth not available" });
    }
  },

  disconnect: async (provider) => {
    try {
      await api.delete(`/api/v1/integrations/${provider}`);
      set((state) => ({
        integrations: state.integrations.map((i) =>
          i.provider === provider ? { ...i, connected: false, account_email: null, enabled: false } : i,
        ),
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Failed to disconnect" });
    }
  },

  handleOAuthCallback: async (code, provider) => {
    try {
      await api.post("/api/v1/integrations/oauth/callback", { code, provider });
      const data = await api.get<{ integrations: IntegrationStatus[] }>("/api/v1/integrations");
      set({ integrations: data.integrations });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "OAuth callback failed" });
    }
  },
}));
